import React, { useEffect, useState, useRef, useCallback } from 'react';
import { SelectOption } from '../../types/selectOption.type';
import {translatedText} from "../TranslatedText";
import './MultiSelectFilter.scss';
import DifferentColoredPlus from "../../assets/icons/differentColoredPlus.svg";

interface Props {
    options: Array<SelectOption>;
    selectedValues: Array<SelectOption>;
    onChange: (values: Array<SelectOption>) => void;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    className?: string;
    isOpen?: boolean;
    onToggle?: () => void;
    showCountInstead?: boolean;
    orderInputs?: {[key: number]: number};
    onOrderChange?: (id: number, order: number) => void;
}

const MultiSelectFilter: React.FC<Props> = (props: Props) => {
    const [internalOpened, setInternalOpened] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>(props.options);

    // Use controlled state if provided, otherwise use internal state
    const opened = props.isOpen !== undefined ? props.isOpen : internalOpened;
    const setOpened = props.onToggle !== undefined ? props.onToggle : setInternalOpened;

    useEffect(() => {
        setFilteredOptions(props.options);
    }, [props.options]);

    useEffect(() => {
        if (searchTerm !== '') {
            const filtered = props.options.filter((option) =>
                option.label?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredOptions(filtered);
        } else {
            setFilteredOptions(props.options);
        }
    }, [searchTerm, props.options]);

    const handleToggleOption = (option: SelectOption) => {
        const isSelected = props.selectedValues.some((val) => val.value === option.value);

        if (isSelected) {
            props.onChange(props.selectedValues.filter((val) => val.value !== option.value));
        } else {
            props.onChange([...props.selectedValues, option]);
        }
    };

    const handleLabelClick = (e: React.MouseEvent, option: SelectOption) => {
        e.preventDefault();
        e.stopPropagation();
        handleToggleOption(option);
    };

    const handleSelectAll = () => {
        props.onChange([...filteredOptions]);
    };

    const handleDeselectAll = () => {
        props.onChange([]);
    };

    const handleRemoveChip = (e: React.MouseEvent, option: SelectOption) => {
        e.stopPropagation();
        props.onChange(props.selectedValues.filter((val) => val.value !== option.value));
    };

    const toggleDropdown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        if (!props.disabled) {
            if (props.onToggle) {
                // Controlled mode - just call the toggle function
                props.onToggle();
            } else {
                // Uncontrolled mode - update internal state
                setInternalOpened(prev => !prev);
            }
        }
    };

    return (
        <div className={`select-wrapper default ${props.className || ''}`}>
            {props.label && <div className="children-container">{props.label}</div>}

            <div className={`select ${props.disabled ? 'disabled' : ''}`}>
                <div className="selected-value-container">
                    <p onClick={toggleDropdown}>
                        {props.selectedValues.length === 0 ? (
                            <span className="placeholder">{props.placeholder || translatedText({id:"selectPlaceholder",defaultMessage:"Zgjidhni..."})}</span>
                        ) : props.showCountInstead ? (
                            <span>
                                {props.selectedValues.length} {translatedText({id:"processes",defaultMessage:"procese"})}
                            </span>
                        ) : (
                            <div className="chips-container">
                                {props.selectedValues.map((selected, index) => (
                                    <div key={index} className="chip">
                                        <span className="chip-label">{selected.label}</span>
                                        <img
                                            src={DifferentColoredPlus}
                                            className="chip-remove"
                                            alt="remove"
                                            onClick={(e) => handleRemoveChip(e, selected)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </p>
                    {!props.disabled && (
                        <svg
                            className={opened ? 'rotatedArrow' : ''}
                            height="10"
                            viewBox="0 0 16 10"
                            width="16"
                            onClick={toggleDropdown}
                        >
                            <path
                                d="m226 21 6 6 6-6"
                                fill="none"
                                stroke="#afafb1"
                                strokeLinecap="square"
                                strokeWidth="2"
                                transform="translate(-224 -19)"
                            />
                        </svg>
                    )}
                </div>

                {opened && (
                    <div className="options-container pt-0">
                        <div className="option searchBarContainer">
                            <input
                                type="text"
                                placeholder={translatedText({id:"searchFilter",defaultMessage:"Kërko..."})}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="action-buttons-row">
                            <button
                                type="button"
                                className="action-btn"
                                onClick={handleSelectAll}
                            >
                                {translatedText({id:"selectAll",defaultMessage:"Zgjidh të gjithë"})}
                            </button>
                            <button
                                type="button"
                                className="action-btn"
                                onClick={handleDeselectAll}
                            >
                                {translatedText({id:"deselectAll",defaultMessage:"Hiq të gjithë"})}
                            </button>
                        </div>

                        <div className="options-list-wrapper">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option, index) => {
                                    const isSelected = props.selectedValues.some(
                                        (val) => val.value === option.value
                                    );
                                    const showOrderInputs = props.orderInputs && props.onOrderChange;

                                    return (
                                        <div
                                            key={index}
                                            className={`option ${isSelected ? 'selectedOption' : ''}`}
                                            onClick={(e) => handleLabelClick(e, option)}
                                            style={{display: 'flex', alignItems: 'center', gap: '10px'}}
                                        >
                                            {showOrderInputs && isSelected ? (
                                                // Show order number instead of checkbox
                                                <div
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        width: '3vh',
                                                        height: '3vh',
                                                        minWidth: '25px',
                                                        minHeight: '25px',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        textAlign: 'center',
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        fontSize: 'clamp(12px, 1.8vh, 16px)',
                                                        flexShrink: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        position: 'relative'
                                                    }}
                                                >
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={props.orderInputs[option.value as number] || 1}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            props.onOrderChange!(option.value as number, parseInt(e.target.value) || 1);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            border: 'none',
                                                            background: 'transparent',
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            fontSize: 'clamp(12px, 1.8vh, 16px)',
                                                            textAlign: 'center',
                                                            outline: 'none',
                                                            padding: 0,
                                                            margin: 0,
                                                            appearance: 'textfield',
                                                            MozAppearance: 'textfield',
                                                            WebkitAppearance: 'none'
                                                        }}
                                                    />
                                                    <style>
                                                        {`
                                                            input[type="number"]::-webkit-inner-spin-button,
                                                            input[type="number"]::-webkit-outer-spin-button {
                                                                -webkit-appearance: none;
                                                                margin: 0;
                                                            }
                                                        `}
                                                    </style>
                                                </div>
                                            ) : (
                                                // Show regular checkbox
                                                <>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        readOnly
                                                    />
                                                    <span className="checkbox-custom"></span>
                                                </>
                                            )}
                                            <span className="option-label" style={{flex: 1}}>{option.label}</span>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="no-options">{translatedText({id:"noOptions",defaultMessage:"Nuk ka të dhëna"})}</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MultiSelectFilter;
